import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query"

import { api } from "@/api"
import { ENDPOINTS } from "@/constants"
import PERMISSION_ROLES from "@/constants/permission-roles"
import { usePermission } from "@/contexts/permission"
import { toast } from "@/hooks/use-toast"
import i18n from "@/i18n"
interface UseGetWebhookResourcesParams {
  webhookId: string
  filters: WebhookResourcesFilters
}
export const getWebhookResources = async ({
  webhookId,
  filters,
}: UseGetWebhookResourcesParams) => {
  const params = new URLSearchParams()
  if (filters.limit)
    params.set("limit", filters.limit.toString())
  if (filters.offset)
    params.set("offset", filters.offset.toString())
  if (filters.resourceId)
    params.set("resourceId", filters.resourceId)
  if (filters.status) params.set("status", filters.status)
  if (filters.resourceType)
    params.set("resourceType", filters.resourceType)
  if (filters.startDate)
    params.set("startDate", filters.startDate)
  if (filters.endDate)
    params.set("endDate", filters.endDate)
  const url = `${ENDPOINTS.WEBHOOKS}/${webhookId}/resources?${params.toString()}`
  const { data } =
    await api.get<WebhookResourcesResponse>(url)
  return data
}
export const useGetWebhookResources = ({
  webhookId,
  filters,
}: UseGetWebhookResourcesParams) => {
  return useQuery({
    queryKey: [
      ENDPOINTS.WEBHOOKS,
      webhookId,
      "resources",
      filters,
    ],
    queryFn: () =>
      getWebhookResources({ webhookId, filters }),
    enabled: !!webhookId,
  })
}
export const getWebhookResourceById = async (
  webhookId: string,
  resourceId: string
) => {
  const url = `${ENDPOINTS.WEBHOOKS}/${webhookId}/resources/${resourceId}`
  const { data } = await api.get<WebhookResource>(url)
  return data
}
export const useGetWebhookResourceById = (
  webhookId: string,
  resourceId: string
) => {
  return useQuery({
    queryKey: [
      ENDPOINTS.WEBHOOKS,
      webhookId,
      "resources",
      resourceId,
    ],
    queryFn: () =>
      getWebhookResourceById(webhookId, resourceId),
    enabled: !!webhookId && !!resourceId,
  })
}
interface ResendWebhookResourcesParams {
  webhookConfId: string
  webhookId: string
}
export const resendWebhookResources = async ({
  webhookConfId,
  webhookId,
}: ResendWebhookResourcesParams) => {
  const url = `${ENDPOINTS.WEBHOOKS}/${webhookConfId}/resources?webhookId=${webhookId}`
  const { data } = await api.put(url)
  return data
}
export const useResendWebhookResources = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: resendWebhookResources,
    onSuccess: (_, variables) => {
      toast({
        title: i18n.t("webhooks.resourceResendSuccess"),
        variant: "success",
      })
      queryClient.invalidateQueries({
        queryKey: [
          ENDPOINTS.WEBHOOKS,
          variables.webhookConfId,
          "resources",
        ],
      })
    },
    onError: () => {
      toast({
        title: i18n.t("webhooks.resourceResendError"),
        variant: "destructive",
      })
    },
  })
}

const ORDER_INSURANCE_EXPIRED_EVENT =
  "ORDER_INSURANCE_EXPIRED"

export const useHasOrderInsuranceExpired = (
  orderId: string | undefined
) => {
  const { hasPermission } = usePermission()
  const canAccessWebhooks = hasPermission([
    ...PERMISSION_ROLES.WEBHOOKS,
  ])

  const { data: webhooksData } = useQuery({
    queryKey: [
      ENDPOINTS.WEBHOOKS,
      "find-insurance-expired",
    ],
    queryFn: async () => {
      const { data } =
        await api.get<WebhookConfigurationsResponseType>(
          ENDPOINTS.WEBHOOKS
        )
      return data
    },
    select: (data) =>
      data?.configurations?.find((webhook) =>
        webhook.events?.includes(
          ORDER_INSURANCE_EXPIRED_EVENT
        )
      ),
    enabled: canAccessWebhooks,
    retry: false,
  })

  const webhookId = webhooksData?.id

  const query = useQuery({
    queryKey: [
      ENDPOINTS.WEBHOOKS,
      webhookId,
      "resources",
      ORDER_INSURANCE_EXPIRED_EVENT,
      orderId,
    ],
    queryFn: () =>
      getWebhookResources({
        webhookId: webhookId!,
        filters: {
          limit: 10,
          offset: 1,
          resourceId: orderId,
          resourceType: ORDER_INSURANCE_EXPIRED_EVENT,
        },
      }),
    enabled: canAccessWebhooks && !!orderId && !!webhookId,
    retry: false,
  })

  const hasExpired = !canAccessWebhooks
    ? false
    : query.isError
      ? false
      : (query.data?.resources?.length ?? 0) > 0

  return { data: hasExpired }
}
